export type PermissionScope =
  | "organization"
  | "product"
  | "department"
  | "agent"
  | "task"
  | "tool"
  | "knowledge";
export type PermissionAction =
  "create" | "read" | "update" | "delete" | "assign" | "execute" | "admin";
export type Permission = `${PermissionScope}:${PermissionAction}` | "*";

export type PermissionSubject = {
  userId: string;
  email: string;
  organizationId: string;
  roles: string[];
  permissions: Permission[];
};

export type PermissionResource = {
  scope: PermissionScope;
  organizationId: string;
  productId?: string;
  departmentId?: string;
  agentId?: string;
  taskId?: string;
  toolId?: string;
};

export type PermissionDecision =
  | {
      allowed: true;
      reason: "owner" | "explicit-permission" | "role-permission";
    }
  | {
      allowed: false;
      reason: "cross-organization" | "missing-permission" | "forbidden-delete";
    };

const rolePermissions: Record<string, Permission[]> = {
  owner: ["*"],
  executive: [
    "organization:read",
    "product:read",
    "department:read",
    "agent:read",
    "task:read",
    "knowledge:read",
  ],
  department_chief: [
    "department:read",
    "agent:read",
    "task:create",
    "task:assign",
    "knowledge:create",
    "knowledge:read",
  ],
};

export class EnterprisePermissionEvaluator {
  evaluate(
    subject: PermissionSubject,
    resource: PermissionResource,
    action: PermissionAction,
  ): PermissionDecision {
    if (subject.organizationId !== resource.organizationId) {
      return { allowed: false, reason: "cross-organization" };
    }

    if (action === "delete" && !subject.roles.includes("owner")) {
      return { allowed: false, reason: "forbidden-delete" };
    }

    if (subject.roles.includes("owner")) {
      return { allowed: true, reason: "owner" };
    }

    const required: Permission = `${resource.scope}:${action}`;
    if (this.hasPermission(subject.permissions, required)) {
      return { allowed: true, reason: "explicit-permission" };
    }

    const inherited = subject.roles.flatMap(
      (role) => rolePermissions[role] ?? [],
    );
    if (this.hasPermission(inherited, required)) {
      return { allowed: true, reason: "role-permission" };
    }

    return { allowed: false, reason: "missing-permission" };
  }

  private hasPermission(
    permissions: Permission[],
    required: Permission,
  ): boolean {
    return permissions.includes("*") || permissions.includes(required);
  }
}
